import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, MapPin, Globe, Search, Ship, Plane, Truck, CheckCircle, Mail, Star, Award, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl } from '../utils/imageUrl';

const Directory = () => {
  const [companies, setCompanies] = useState([]);
  const [trustScores, setTrustScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [filterMode, setFilterMode] = useState('all');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
      
      // Fetch trust scores for each company
      const scores = {};
      for (const company of response.data.slice(0, 10)) {
        try {
          const scoreRes = await api.get(`/companies/${company.id}/trust-score`);
          scores[company.id] = scoreRes.data;
        } catch (e) {
          scores[company.id] = { trust_score: 0, rating: 'New Member' };
        }
      }
      setTrustScores(scores);
    } catch (error) {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  };

  // Deduplicate companies by normalized name (removes case duplicates)
  const deduplicatedCompanies = companies.reduce((acc, company) => {
    const normalizedName = company.legal_name.toLowerCase().replace(/\s+/g, ' ').trim();
    const existingIndex = acc.findIndex(c => 
      c.legal_name.toLowerCase().replace(/\s+/g, ' ').trim() === normalizedName
    );
    if (existingIndex === -1) {
      acc.push(company);
    } else {
      // Keep the one with the most complete profile
      const existing = acc[existingIndex];
      const existingScore = (existing.services?.length || 0) + (existing.modes?.length || 0);
      const newScore = (company.services?.length || 0) + (company.modes?.length || 0);
      if (newScore > existingScore) {
        acc[existingIndex] = company;
      }
    }
    return acc;
  }, []);

  const filteredCompanies = deduplicatedCompanies.filter(company => {
    const matchesSearch = company.legal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (company.dba && company.dba.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCountry = filterCountry === 'all' || company.country === filterCountry;
    const matchesService = filterService === 'all' || company.services.includes(filterService);
    const matchesMode = filterMode === 'all' || (company.modes && company.modes.includes(filterMode));
    return matchesSearch && matchesCountry && matchesService && matchesMode;
  });

  const countries = [...new Set(deduplicatedCompanies.map(c => c.country))].sort();
  const services = [...new Set(deduplicatedCompanies.flatMap(c => c.services || []))].sort();
  const modes = [...new Set(deduplicatedCompanies.flatMap(c => c.modes || []))].sort();

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return <Plane className="h-3 w-3" />;
      case 'sea': return <Ship className="h-3 w-3" />;
      case 'land': return <Truck className="h-3 w-3" />;
      default: return null;
    }
  };

  const getTrustBadge = (companyId) => {
    const score = trustScores[companyId];
    if (!score) return null;
    
    const colorMap = {
      'Excellent': 'bg-green-100 text-green-700',
      'Good': 'bg-blue-100 text-blue-700',
      'Fair': 'bg-amber-100 text-amber-700',
      'New Member': 'bg-slate-100 text-slate-700'
    };
    
    return (
      <Badge className={`flex items-center gap-1 ${colorMap[score.rating] || 'bg-slate-100'}`}>
        <Star className="h-3 w-3" />
        {score.trust_score}
      </Badge>
    );
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900">Member Directory</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">
              {companies.length} verified freight professionals across the Caribbean
            </p>
          </div>

          {/* Filters */}
          <Card className="mb-4 sm:mb-6">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by company name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 text-sm"
                    data-testid="directory-search"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <Select value={filterCountry} onValueChange={setFilterCountry}>
                    <SelectTrigger className="text-xs sm:text-sm">
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterService} onValueChange={setFilterService}>
                    <SelectTrigger className="text-xs sm:text-sm">
                      <SelectValue placeholder="Service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      {services.map(service => (
                        <SelectItem key={service} value={service} className="capitalize">
                          {service.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterMode} onValueChange={setFilterMode}>
                    <SelectTrigger className="text-xs sm:text-sm">
                      <SelectValue placeholder="Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      {modes.map(mode => (
                        <SelectItem key={mode} value={mode} className="capitalize">
                          {mode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results count */}
          <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">
            Showing {filteredCompanies.length} of {companies.length} companies
          </p>

          {/* Company Grid */}
          {filteredCompanies.length === 0 ? (
            <Card>
              <CardContent className="p-8 sm:p-12 text-center text-slate-600">
                <Building2 className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-slate-400" />
                <p className="mb-2 text-sm sm:text-base">No companies found</p>
                <p className="text-xs sm:text-sm">Try adjusting your search or filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {filteredCompanies.map((company) => (
                <Card key={company.id} className="hover:shadow-lg transition-all hover:border-cyan-200" data-testid={`company-card-${company.id}`}>
                  <CardHeader className="p-3 sm:p-4 lg:p-6 pb-2 sm:pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        {toFullUrl(company.logo_url) ? (
                          <img src={toFullUrl(company.logo_url)} alt={company.legal_name} className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-slate-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <CardTitle className="text-sm sm:text-base lg:text-lg truncate">{company.legal_name}</CardTitle>
                          {company.dba && (
                            <p className="text-[10px] sm:text-xs text-slate-500 truncate">DBA: {company.dba}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 items-end flex-shrink-0">
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1 text-[10px] sm:text-xs">
                          <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          Verified
                        </Badge>
                        {getTrustBadge(company.id)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600 mt-2">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-500" />
                      <span>{company.country}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 lg:p-6 pt-0 sm:pt-0">
                    <div className="space-y-2 sm:space-y-3">
                      {company.description && (
                        <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{company.description}</p>
                      )}

                      <div>
                        <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Services</p>
                        <div className="flex flex-wrap gap-1">
                          {company.services.slice(0, 3).map(service => (
                            <Badge key={service} variant="secondary" className="text-[10px] sm:text-xs capitalize">
                              {service.replace('_', ' ')}
                            </Badge>
                          ))}
                          {company.services.length > 3 && (
                            <Badge variant="secondary" className="text-[10px] sm:text-xs">
                              +{company.services.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Transport Modes</p>
                        <div className="flex flex-wrap gap-1">
                          {company.modes.map(mode => (
                            <Badge key={mode} className="text-[10px] sm:text-xs bg-cyan-100 text-cyan-700 flex items-center gap-1">
                              {getModeIcon(mode)}
                              <span className="capitalize">{mode}</span>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {company.certifications && company.certifications.length > 0 && (
                        <div>
                          <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Certifications</p>
                          <p className="text-[10px] sm:text-xs text-slate-700">
                            {company.certifications.map(cert => 
                              typeof cert === 'object' ? cert.name : cert
                            ).filter(Boolean).join(', ')}
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2 pt-3 border-t">
                        <Link to={`/company/${company.id}`} className="flex-1">
                          <Button size="sm" className="w-full text-xs bg-cyan-600 hover:bg-cyan-700">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View Profile
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm" className="flex-1 text-xs">
                          <Mail className="h-3 w-3 mr-1" />
                          Contact
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Directory;
